import React, { useEffect, useState, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { vendorDetails, vendorOfferList, vendorServiceList } from '../../redux/slices/vendor'
import { useDispatch, useSelector } from 'react-redux'
import { Carousel, Badge, Modal } from 'react-bootstrap'
import {
    MapPin, Phone, Mail, Clock, Globe, MessageCircle,
    Facebook, Instagram, Twitter, Linkedin,
    Scissors, Star, Calendar, Store,
    BadgePercent, ChevronLeft, ChevronRight,
    CircleUser,
    Navigation,
    Link,
    Share2
} from 'lucide-react'
import { toast } from 'react-toastify'
import moment from 'moment'
import { serviceCategory } from '../../redux/slices/vendor/ServiceCategory'
import { serviceSubCategory } from '../../redux/slices/vendor/ServiceSubCategory'
import { profileServices } from '../../redux/slices/vendor/ProfileServices'
import { profileDetails } from '../../redux/slices/vendor/ProfileDetails'
import { profileOfferList } from '../../redux/slices/vendor/ProfileOffer'

export default function VendorProfile() {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [servicesLoading, setServicesLoading] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState('')
    const [selectedService, setSelectedService] = useState('')
    const [serverTime, setServerTime] = useState(null)
    const scrollRef = useRef(null)
    const locationRef = useRef(null)
    const [showModal, setShowModal] = useState(false)
    const [selectedImg, setSelectedImg] = useState('')

    const scroll = (direction) => {
        if (scrollRef.current) {
            const { current } = scrollRef
            const scrollAmount = direction === 'left' ? -350 : 350
            current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
        }
    }

    // Selectors
    const details = useSelector(state => state?.profileDetails?.data?.[0])
    const offers = useSelector(state => state?.profileOfferList?.data)
    const services = useSelector(state => state?.profileServices?.data)
    const category = useSelector(state => state?.serviceCategory?.data)
    const sub_category = useSelector(state => state?.serviceSubCategory?.data)

    const handle_sub_category = (category_id) => {
        setServicesLoading(true)
        setSelectedCategory(category_id)
        dispatch(serviceSubCategory({ navigate, category_id, shop_id: details?.id })).then((res) => {
            if (res?.payload?.status === 200) {
                const subCatId = res?.payload?.data?.data?.[0]?.id;
                setSelectedService(subCatId)
                dispatch(profileServices({ navigate, sub_category_id: subCatId, shop_id: details?.id })).then(() => {
                    setServicesLoading(false)
                })
            } else {
                setServicesLoading(false)
            }
        }).catch(() => setServicesLoading(false))
    }

    const handle_services = (service_id) => {
        setServicesLoading(true)
        setSelectedService(service_id)
        dispatch(profileServices({ navigate, sub_category_id: service_id, shop_id: details?.id })).then(() => {
            setServicesLoading(false)
        }).catch(() => setServicesLoading(false))
    }

    useEffect(() => {
        setLoading(true)
        dispatch(profileDetails({ 'id': id, navigate })).then((res) => {
            console.log("Profile Details Response:", res?.payload?.data);
            if (res?.payload?.status === 200) {
                // Try getting currentDateTime from root or data array
                const rootTime = res?.payload?.data?.currentDateTime;
                const dataTime = res?.payload?.data?.data?.[0]?.currentDateTime;
                setServerTime(rootTime || dataTime);
            }
            details_res(res.payload)
        })
    }, [])

    const details_res = async (res) => {
        try {
            if (res?.status === 200) {
                const vendorData = res?.data?.data?.[0];
                if (vendorData) {
                    const [catRes] = await Promise.all([
                        dispatch(serviceCategory({ navigate, shop_id: vendorData?.id })),
                        dispatch(profileOfferList({ navigate, shop_id: vendorData?.id }))
                    ]);

                    if (catRes?.payload?.status === 200) {
                        const categories = catRes?.payload?.data?.data;
                        if (categories?.length > 0) {
                            const firstCatId = categories?.[0]?.id;
                            setSelectedCategory(firstCatId)

                            const subCatRes = await dispatch(serviceSubCategory({ navigate, category_id: firstCatId }));

                            if (subCatRes?.payload?.status === 200) {
                                const subCategories = subCatRes?.payload?.data?.data;
                                setSelectedService(subCategories?.[0]?.id || null)
                                if (subCategories?.length > 0) {
                                    const firstSubCatId = subCategories?.[0]?.id;
                                    dispatch(profileServices({ navigate, sub_category_id: firstSubCatId }));
                                }
                            }
                        }
                    }
                }
                setLoading(false)
            } else {
                setLoading(false)
            }
        } catch (error) {
            console.error(error)
            setLoading(false)
        }
    }

    // Auto-select first category when loaded
    useEffect(() => {
        if (category?.length > 0 && !selectedCategory) {
            handle_sub_category(category?.[0]?.id)
        }
    }, [category])

    // Set Dynamic Title and Description
    const pageTitle = details?.business_name || "Salon Management System & Software | My Salon Hub";
    const metaContent = details?.business_name
        ? `Visit ${details.business_name} for expert hair styling, beauty treatments, and relaxing services. Affordable prices with top-quality care.`
        : "Streamline your beauty business with our all-in-one salon management system. Features include online booking, POS, and inventory tracking. Start a free trial!";


    const getSocialLinks = () => {
        try {
            const jsonStr = details?.social_links;
            if (!jsonStr) return {};
            return typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
        } catch (e) {
            return {};
        }
    }
    const socialLinks = getSocialLinks();

    const handleShare = async () => {
        const profileUrl = `${process.env.REACT_APP_BASE_URL}profile/${details?.vendor_id}`;
        const shareText = `Hello 😊 Dear Customer,
We are always at your service!
 
🌸 " AT ${details?.business_name}."
 
हाई-क्वालिटी वाली मॉडर्न ब्यूटी सेवाएं, आपके बजट के अनुसार "हर बार शानदार अनुभव"
 
🎁 आज ही अपनी अपॉइंटमेंट बुक करें और पाएँ आकर्षक स्पेशल ऑफ़र!
📞 WhatsApp मैसेज/कॉल करें: ${details?.phone || details?.user?.phone_num}
 
😊 हमारा नया ऑनलाइन मेन्यू कार्ड देखने के लिए
📍 Visit करें: 👉 ${profileUrl}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: details?.business_name,
                    text: shareText,
                });
            } catch (err) {
                console.log("Error sharing:", err);
            }
        } else {
            // Fallback for desktop or non-supported browsers
            navigator.clipboard.writeText(shareText).then(() => {
                toast.success("Profile details copied to clipboard!", { position: "bottom-right" });
            }).catch(err => {
                toast.error("Failed to copy details", { position: "bottom-right" });
            });
        }
    }

    // Helper to construct image URLs (using env as per user's edit)
    const BASE_URL = process.env.REACT_APP_BASE_URL || '';

    const getStatus = () => {
        if (!serverTime || !details?.open_time || !details?.close_time) return null;
        const currentTime = serverTime?.split(' ')[1];
        if (!currentTime) return null;
        const convertToSeconds = (timeStr) => {
            const [h, m, s] = timeStr?.split(':')?.map(Number);
            return h * 3600 + m * 60 + s;
        };
        const currentSeconds = convertToSeconds(currentTime);
        const openSeconds = convertToSeconds(details?.open_time);
        const closeSeconds = convertToSeconds(details?.close_time);

        if (openSeconds <= closeSeconds) {
            return currentSeconds >= openSeconds && currentSeconds <= closeSeconds;
        } else {
            return currentSeconds >= openSeconds || currentSeconds <= closeSeconds;
        }
    }
    const isOpen = getStatus();

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    return (
        <div className="container-fluid p-0">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaContent} />
            </Helmet>
            <style>{`
                .responsive-offer { width: 100%; aspect-ratio: 2 / 1; height: auto; }
                .responsive-banner { width: 100%; aspect-ratio: 2 / 1; height: auto; }
                .responsive-margin { margin-top: 0px; }
                .responsive-avatar { width: 160px; height: 160px; }
                .info-section { border-bottom: 1px solid #e9ecef; }
                .info-section:last-child { border-bottom: none; }
                .responsive-map { height: 450px; }
                
                @media (min-width: 768px) {
                    .info-section { border-bottom: none; border-right: 1px solid #e9ecef; }
                    .info-section:last-child { border-right: none; }
                }

                @media (max-width: 768px) {
                    .responsive-offer { width: 100%; aspect-ratio: 2 / 1; height: auto; }
                    .responsive-banner { width: 100%; aspect-ratio: 2 / 1; height: auto; }
                    .responsive-margin { margin-top: 0px; }
                    .responsive-avatar { width: 120px; height: 120px; }
                    .responsive-map { height: 300px; }
                    .display-6 { font-size: 1.5rem; }
                }
            `}</style>

            <div className="shadow">
                <div className='responsive-banner'>
                    <img
                        className="d-block w-100 h-100 object-fit-cover"
                        src={BASE_URL + (details?.shopPicture)}
                        alt={details?.business_name || "Banner"}
                    // onError={(e) => e.target.src = "https://via.placeholder.com/1200x400?text=Banner"}
                    />
                </div>
            </div>

            <div className="bg-white">
                <div className="container pb-5">

                    {/* 2. Basic Details (Center Section) */}
                    {details && (
                        <div className="row justify-content-center mb-5 position-relative responsive-margin">
                            <div className="col-lg-10">
                                <div className="text-center mb-1 mt-4">
                                    <h2 className="fw-bolder mb-1 display-6 text-capitalize" style={{ color: '#ff90a0' }}><Store size={30} className='me-2 mb-2' />{details?.business_name}</h2>
                                    <h6 className="mb-3 text-muted">{details?.address}</h6>
                                    <div className="d-flex justify-content-center gap-2 mt-4 gap-md-3 mb-3 px-1">
                                        {/* Timing Card */}
                                        <div className="p-2 rounded-4 shadow-sm border-0 d-flex flex-column align-items-center justify-content-center" style={{ backgroundColor: '#fff5f5', flex: '1', maxWidth: '240px' }}>
                                            <h6 className="fw-bold mb-1 text-center d-flex align-items-center" style={{ color: '#10b981', fontSize: '11px' }}>
                                                Service Timings
                                                {/* {isOpen !== null && (
                                                    <span className={`ms-1 rounded-circle bg-${isOpen ? 'success' : 'danger'}`} style={{ width: '6px', height: '6px' }} title={isOpen ? 'Open' : 'Closed'}></span>
                                                )} */}
                                            </h6>
                                            <div className="d-flex align-items-center bg-white rounded-pill px-2 py-1 shadow-sm">
                                                <Clock size={12} className="me-1 text-dark" />
                                                <span className="text-dark" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                    {details?.open_time ? moment(details?.open_time, "HH:mm:ss").format("hh:mm A") : ''} - {details?.close_time ? moment(details?.close_time, "HH:mm:ss").format("hh:mm A") : ''}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Get Direction Card */}
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${details?.latitude},${details?.longitude}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="p-2 rounded-4 shadow-sm border-0 d-flex flex-column align-items-center justify-content-center text-decoration-none"
                                            style={{ backgroundColor: '#fff5f5', flex: '1', maxWidth: '240px' }}
                                        >
                                            <div className="mb-1 d-flex align-items-center justify-content-center rounded-circle shadow-sm bg-white" style={{ width: '32px', height: '32px' }}>
                                                <Navigation size={18} className="text-danger" />
                                            </div>
                                            <span className="fw-bold text-dark text-center" style={{ fontSize: '12px' }}>Get Direction</span>
                                            {/* <Navigation size={14} className="text-dark opacity-50 mt-1" /> */}
                                        </a>
                                    </div>

                                    <div className="d-flex justify-content-center gap-3 mt-4 gap-sm-5">
                                        {/* Contact */}
                                        <a href={`tel:${details?.phone || details?.user?.phone_num}`} className="text-decoration-none text-center group">
                                            <div className="d-inline-flex justify-content-center align-items-center bg-info bg-opacity-10 rounded-circle mb-2 transition-transform hover-scale" style={{ width: '60px', height: '60px' }}>
                                                <Phone className="text-info" size={25} />
                                            </div>
                                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '12px' }}>Contact</h6>
                                        </a>

                                        {/* WhatsApp */}
                                        <a href={`https://wa.me/${details?.phone || details?.user?.phone_num}`} target="_blank" rel="noreferrer" className="text-decoration-none text-center group">
                                            <div className="d-inline-flex justify-content-center align-items-center bg-success bg-opacity-10 rounded-circle mb-2 transition-transform hover-scale" style={{ width: '60px', height: '60px' }}>
                                                <MessageCircle className="text-success" size={25} />
                                            </div>
                                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '12px' }}>Whatsapp</h6>
                                        </a>

                                        {/* Instagram instead of Location */}
                                        {socialLinks?.instagram && (
                                            <a href={socialLinks.instagram} target="_blank" rel="noreferrer" className="text-decoration-none text-center group">
                                                <div className="d-inline-flex justify-content-center align-items-center bg-danger bg-opacity-10 rounded-circle mb-2 transition-transform hover-scale" style={{ width: '60px', height: '60px' }}>
                                                    <Instagram className="text-danger" size={25} />
                                                </div>
                                                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '12px' }}>Instagram</h6>
                                            </a>
                                        )}

                                        {!socialLinks?.instagram && (
                                            <div
                                                onClick={() => locationRef?.current?.scrollIntoView({ behavior: 'smooth' })}
                                                className="text-center group"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-inline-flex justify-content-center align-items-center bg-info bg-opacity-10 rounded-circle mb-2 transition-transform hover-scale" style={{ width: '60px', height: '60px' }}>
                                                    <MapPin className="text-info" size={25} />
                                                </div>
                                                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '12px' }}>Location</h6>
                                            </div>
                                        )}

                                        {/* Share Option */}
                                        <div
                                            onClick={handleShare}
                                            className="text-center group"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-inline-flex justify-content-center align-items-center bg-primary bg-opacity-10 rounded-circle mb-2 transition-transform hover-scale" style={{ width: '60px', height: '60px' }}>
                                                <Share2 className="text-primary" size={25} />
                                            </div>
                                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '12px' }}>Share Profile</h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {offers?.length ?
                        <div className="mb-5">
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                <h5 className="fw-bold m-0" style={{ color: '#ff90a0' }}><BadgePercent className="me-2 mb-1" size={24} style={{ color: '#ff90a0' }} />Our Offers</h5>
                            </div>

                            {/* Unified View: Horizontal Scroll */}
                            <div className="position-relative">
                                {/* Scroll Buttons (Desktop Only) */}
                                {offers?.length > 1 && (
                                    <>
                                        <button
                                            onClick={() => scroll('left')}
                                            className="btn btn-light rounded-circle shadow position-absolute top-50 translate-middle-y z-3 d-none d-md-flex justify-content-center align-items-center opacity-75 hover-opacity-100"
                                            style={{ width: '40px', height: '40px', left: '10px' }}
                                        >
                                            <ChevronLeft size={24} />
                                        </button>
                                        <button
                                            onClick={() => scroll('right')}
                                            className="btn btn-light rounded-circle shadow position-absolute top-50 translate-middle-y z-3 d-none d-md-flex justify-content-center align-items-center opacity-75 hover-opacity-100"
                                            style={{ width: '40px', height: '40px', right: '10px' }}
                                        >
                                            <ChevronRight size={24} />
                                        </button>
                                    </>
                                )}

                                <div ref={scrollRef} className="d-flex overflow-auto pb-2 no-scrollbar px-1" style={{ gap: '16px', scrollSnapType: 'x mandatory', }}>
                                    <style>{`
                                        .no-scrollbar::-webkit-scrollbar { display: none; } 
                                        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                                        .offer-card { width: 90vw; aspect-ratio: 2 / 1; height: auto; }
                                        @media (min-width: 768px) { .offer-card { width: 70vw; aspect-ratio: 2 / 1; height: auto; } }
                                        .hover-opacity-100:hover { opacity: 1 !important; }
                                    `}</style>
                                    {offers?.map((offer) => (
                                        <div
                                            key={offer?.id}
                                            className="position-relative overflow-hidden flex-shrink-0 offer-card"
                                            style={{
                                                borderRadius: '20px',
                                                scrollSnapAlign: 'start',
                                                backgroundColor: 'white',
                                                boxShadow: " rgba(0, 0, 0, 0.16) 0px 3px 6px, rgba(0, 0, 0, 0.23) 0px 3px 6px",
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => {
                                                setSelectedImg(BASE_URL + offer?.banner_image)
                                                setShowModal(true)
                                            }}
                                        >
                                            <img
                                                src={BASE_URL + offer?.banner_image}
                                                alt={offer?.offer_details}
                                                className="w-100 h-100 object-fit-cover position-absolute top-0 start-0"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.style.background = "white";
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div> : ''}

                    {/* Booking CTA Section */}
                    <div className="mb-5 mt-2">
                        <a
                            href={`tel:${details?.phone || details?.user?.phone_num}`}
                            className="text-decoration-none"
                        >
                            <div className="p-3 rounded-4 shadow-sm d-flex align-items-center justify-content-between px-3 px-md-4 transition-transform hover-scale" style={{ backgroundColor: '#fff5f5', border: '1.5px dashed #ff90a0' }}>
                                <div className="d-flex align-items-center">
                                    <div className="bg-white rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '45px', height: '45px', flexShrink: 0 }}>
                                        <Phone size={22} style={{ color: '#ff90a0' }} />
                                    </div>
                                    <div>
                                        <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>CALL US TO BOOK YOUR APPOINTMENT</h6>
                                        <p className="mb-0 text-muted" style={{ fontSize: '10px' }}>Tap here to connect and book instantly</p>
                                    </div>
                                </div>
                                <div className="bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '28px', height: '28px', flexShrink: 0 }}>
                                    <ChevronRight size={18} style={{ color: '#ff90a0' }} />
                                </div>
                            </div>
                        </a>
                    </div>

                    {/* 3. Services List (Bottom Section) */}

                    <div className="mt-5 mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                            <h5 className="fw-bold m-0 d-flex align-items-center" style={{ color: '#ff90a0' }}>
                                <Scissors className="me-2" size={24} style={{ color: '#ff90a0' }} />
                                Our Services
                            </h5>
                        </div>

                        {/* Category Tabs */}
                        {category?.length > 0 && (
                            <div className="d-flex overflow-auto pb-2 mb-2 no-scrollbar px-1" style={{ gap: '20px', whiteSpace: 'nowrap' }}>
                                {category?.map((cat, index) => (
                                    <h6
                                        key={index}
                                        onClick={() => handle_sub_category(cat?.id)}
                                        className={`px-1 mb-0 pb-2 ${selectedCategory === cat?.id ? 'fw-bold border-bottom border-2' : 'text-muted'}`}
                                        style={{ cursor: 'pointer', transition: 'all 0.2s', borderColor: selectedCategory === cat?.id ? '#ff90a0' : 'transparent', color: selectedCategory === cat?.id ? '#ff90a0' : '' }}
                                    >
                                        {cat?.name}
                                    </h6>
                                ))}
                            </div>
                        )}

                        {/* Sub Category Filter (category_by_main) */}
                        {sub_category?.length > 0 && (
                            <div className="d-flex overflow-auto pb-3 mb-3 no-scrollbar px-1" style={{ gap: '10px', whiteSpace: 'nowrap' }}>

                                {sub_category?.map((sub, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handle_services(sub?.id)}
                                        className={`btn btn-sm rounded-pill px-3 fw-bold`}
                                        style={{
                                            backgroundColor: selectedService === sub?.id ? '#ff90a0' : 'transparent',
                                            color: selectedService === sub?.id ? 'white' : '#ff90a0',
                                            borderColor: '#ff90a0'
                                        }}
                                    >
                                        {sub?.name}
                                    </button>
                                ))}
                            </div>
                        )}

                        {servicesLoading ? (
                            <div className="d-flex justify-content-center align-items-center py-5 w-100">
                                <div className="spinner-border text-primary" role="status" style={{ color: '#ff90a0' }}>
                                    <span className="visually-hidden">Loading services...</span>
                                </div>
                            </div>
                        ) : (
                            (() => {
                                return services?.length > 0 ? (
                                    <div className="d-flex overflow-auto pb-4 px-1 no-scrollbar" style={{ gap: '20px', scrollSnapType: 'x mandatory' }}>
                                        <style>{`
                                    .service-card { width: 180px; padding:10px; border-radius:20px; background-color: white; box-shadow: 0 4px 12px rgba(0,0,0,0.08); scroll-snap-align: start; } 
                                    .service-image-container { height: 160px; border-radius: 20px; position: relative; overflow: hidden; }
                                    .bag-btn { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: white; box-shadow: 0 4px 12px rgba(0,0,0,0.1); position: absolute; bottom: 10px; right: 10px; z-index: 10; cursor: pointer; border: none;}
                                `}</style>
                                        {services?.map((service) => (

                                            <div className="flex-shrink-0 service-card" key={service?.id} style={{ scrollSnapAlign: 'start' }}>
                                                {/* Image Section */}
                                                <div className="service-image-container mb-3 shadow-sm">
                                                    <img
                                                        src={BASE_URL + service?.image}
                                                        alt={service?.name}
                                                        className="w-100 h-100 object-fit-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentElement.style.backgroundColor = '#f8f9fa';
                                                        }}
                                                    />
                                                </div>

                                                {/* Content Section */}
                                                <div>
                                                    <h6 className="fw-bold text-dark mb-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{service?.name}</h6>

                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <h6 className="fw-bold text-dark mb-0">₹{service?.price}</h6>
                                                    </div>
                                                    <div className="d-flex align-items-center text-muted small mt-1">
                                                        <Clock size={12} className="me-1 text-secondary" />
                                                        <span className='text-secondary'>{service?.time?.startsWith('00:') ? `${service?.time?.split(':')[1]} min.` : `${service?.time} min.`}</span>
                                                    </div>
                                                </div>
                                            </div>

                                        ))}
                                    </div>
                                ) : (
                                    !loading && (
                                        <div className="text-center py-5 w-100">
                                            <Scissors size={48} className="text-secondary opacity-50 mb-3" />
                                            <h5 className="text-secondary opacity-75">No services listed at the moment.</h5>
                                        </div>
                                    )
                                );
                            })()
                        )}
                    </div>

                    {/* 4. Location Map */}
                    <div className="mt-4 mb-5 pb-2" ref={locationRef}>
                        <div className="border-bottom pb-2">
                            <div className='d-flex justify-content-between align-items-center'>
                                <h5 className=" m-0 d-flex align-items-center" style={{ color: '#ff90a0' }}>
                                    <MapPin className="me-2" size={24} style={{ color: '#ff90a0' }} />
                                    Location
                                </h5>
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${details?.latitude},${details?.longitude}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn  btn-sm"
                                    style={{ backgroundColor: 'white', color: '#10B981', borderColor: '#10B981' }}
                                >
                                    <Navigation className="me-1 mb-1" size={20} />
                                    Direction
                                </a>
                            </div>


                        </div>
                        {/* <h6 className='text-muted text-capitalize mt-2' style={{ marginLeft: '32px' }}>{details?.address}</h6> */}
                        {details?.latitude && details?.longitude ? (
                            <>
                                <div className="card mt-4 border-0 rounded-4 overflow-hidden" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                                    <iframe
                                        width="100%"
                                        className="responsive-map"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        allowFullScreen
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://maps.google.com/maps?q=${details?.latitude},${details?.longitude}&hl=en&z=14&output=embed`}
                                        title="Vendor Location"
                                    ></iframe>
                                </div>

                            </>
                        ) : (
                            <div className="text-center py-5">
                                <MapPin size={48} className="text-muted opacity-25 mb-3" />
                                <h5 className="text-muted opacity-75">Location coordinates not available.</h5>
                            </div>
                        )}
                    </div>

                    {/* 4. Follow Us */}

                    <div className='mt-5 mb-4 border-bottom pb-2'>
                        <h5 className=" m-0 d-flex align-items-center" style={{ color: '#ff90a0' }}>
                            <Globe className="me-2" size={24} style={{ color: '#ff90a0' }} />
                            Follow Us
                        </h5>
                    </div>
                    <div className="d-flex justify-content-center gap-3 pb-5">
                        {(() => {
                            let links = {};
                            try {
                                links = details?.social_links ? (typeof details?.social_links === 'string' ? JSON.parse(details?.social_links) : details?.social_links) : {};
                            } catch (e) { console.error("Error parsing social links", e); }

                            // Filter out links that are either empty or redirected to the top section
                            const displayLinks = Object.entries(links).filter(([platform, link]) => {
                                return link && !['twitter', 'linkedin', 'instagram'].includes(platform.toLowerCase());
                            });

                            if (displayLinks.length === 0) {
                                return <span className="text-muted small">No other social links available</span>;
                            }

                            return displayLinks.map(([platform, link]) => {
                                let Icon = Globe;
                                let colorClass = "text-secondary";
                                switch (platform.toLowerCase()) {
                                    case 'facebook': Icon = Facebook; colorClass = "text-primary"; break;
                                }
                                return (
                                    <a
                                        key={platform}
                                        href={link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-white shadow-sm d-flex align-items-center justify-content-center hover-scale transition-transform"
                                        style={{ width: '60px', height: '60px', borderRadius: '20px', backgroundColor: 'white' }}
                                    >
                                        <Icon size={24} className={colorClass} />
                                    </a>
                                )
                            })
                        })()}
                    </div>
                </div>
            </div>

            {/* Offer Image Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton className='border-bottom border-light ps-3 pe-3 pt-3 pb-0'>Offer</Modal.Header>
                <Modal.Body className="p-2 text-center">
                    <img
                        src={selectedImg}
                        alt="Offer Banner"
                        className="img-fluid rounded"
                        style={{ maxHeight: '90vh' }}
                    />
                </Modal.Body>
            </Modal>
        </div>
    )
}
